import React, { FunctionComponent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useI18n } from "../../../hooks/useI18n";
import { AppState } from "../../../store";
import { thunkFetchProfitStatistics } from "../../../store/statistics/profit/actions";
import { ProfitsStatisticsState } from "../../../store/statistics/profit/types";
import StatTable from "../components/StatTable";
import DateRangePicker from "../../../components/inputs/daterangepicker";
import BarChart from "../components/BarChart";
import { ExclVat } from "../components/exclVat";
import { Alert } from "@material-ui/lab";
import { Grid, Hidden, Switch, useMediaQuery } from "@material-ui/core";
import { BarChartData } from "../components/BarChart/types";
import TableWrapper from "../components/TableWrapper";
import moment from "moment";
import ProfitDetails from "./ProfitDetails";
import { useTheme } from "@material-ui/core/styles";
import { Currency } from "../../../components/misc/Currency";

export const ProfitStatistics: FunctionComponent = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { t } = useI18n();

  const [dateRange, setDateRange] = useState([
    moment().toDate(),
    moment().toDate()
  ]);

  const [showGraph, setShowGraph] = useState(false);

  const statisticsState = useSelector<AppState, ProfitsStatisticsState>(
    s => s.profitStatistics
  );

  let { profitStatistics, isLoading } = statisticsState;

  useEffect(() => {
    if (dateRange)
      dispatch(thunkFetchProfitStatistics(dateRange[0], dateRange[1]));
  }, [dateRange]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowGraph(event.target.checked);
  };

  const populateProfitPerCategory = () => {
    return profitStatistics.reduce((acc: any, curr) => {
      if (!acc[curr.category]) acc[curr.category] = 0;
      acc[curr.category] += curr.profit;
      return acc;
    }, {});
  };

  let profitPerStore = {} as any;
  const populateProfitPerStore = () => {
    if (Object.entries(profitPerStore).length > 0) return profitPerStore;

    return (profitPerStore = profitStatistics.reduce((acc: any, curr) => {
      if (!acc[curr.storeName]) {
        acc[curr.storeName] = {
          storeId: curr.storeId,
          sales: 0,
          cost: 0,
          waste: 0,
          discount: 0,
          campaign: 0,
          profit: 0,
          categoryProfit: new Map<string, number>()
        };
      }

      if (!acc[curr.storeName].categoryProfit.has(curr.category)) {
        acc[curr.storeName].categoryProfit.set(curr.category, {
          sales: curr.sales,
          cost: curr.cost,
          waste: curr.waste,
          campaign: curr.campaign,
          profit: curr.profit
        });
      } else {
        let category = acc[curr.storeName].categoryProfit.get(curr.category);
        category.sales += curr.sales;
        category.cost += curr.cost;
        category.waste += curr.waste;
        category.campaign += curr.campaign;
        category.profit += curr.profit;
        acc[curr.storeName].categoryProfit.set(curr.category, category);
      }

      acc[curr.storeName].sales += curr.sales;
      acc[curr.storeName].cost += curr.cost;
      acc[curr.storeName].waste += curr.waste;
      acc[curr.storeName].discount += curr.discount;
      acc[curr.storeName].campaign += curr.campaign;
      acc[curr.storeName].profit += curr.profit - curr.discount;
      return acc;
    }, {}));
  };
  const populateProfitTable = () => {
    const storeProfit = populateProfitPerStore();

    return Object.keys(storeProfit).map(p => {
      return {
        storeName: p,
        storeId: storeProfit[p].storeId,
        sales: storeProfit[p].sales,
        cost: storeProfit[p].cost,
        waste: storeProfit[p].waste,
        discount: storeProfit[p].discount,
        campaign: storeProfit[p].campaign,
        profit: storeProfit[p].profit,
        categoryProfit: storeProfit[p].categoryProfit
      };
    });
  };

  const populateChartData = (): BarChartData => {
    const categoryProfits = populateProfitPerCategory();
    return {
      labels: Object.keys(categoryProfits).map(
        c => c.charAt(0).toUpperCase() + c.slice(1)
      ),
      datasets: [
        {
          label: "",
          data: Object.keys(categoryProfits).map(c => categoryProfits[c])
        }
      ]
    };
  };

  return (
    <>
      <DateRangePicker
        disabled={isLoading}
        onChange={setDateRange}
        value={dateRange}
      />
      <ExclVat />
      <Grid
        container
        direction={"row"}
        justifyContent={"space-evenly"}
        alignItems={"flex-start"}
        style={{ marginTop: "2rem" }}
        spacing={2}
      >
        <Grid item xs={12}>
          <TableWrapper title={"Profit"}>
            <Alert severity={"info"}>
              {t("common.messages.salesBeforeDiscounts")}
            </Alert>
            <Hidden smDown>
              <Grid
                style={{
                  position: "absolute",
                  zIndex: 100,
                  width: "160px",
                  margin: "25px 0 0 10px"
                }}
                component="label"
                container
                justifyContent="flex-start"
                alignItems="center"
                spacing={1}
              >
                <Grid item>{t("common.messages.table")}</Grid>
                <Grid item>
                  <Switch
                    size={"small"}
                    checked={showGraph}
                    onChange={handleChange}
                    name="graphSwitch"
                    disabled={isLoading}
                  />
                </Grid>
                <Grid item>{t("common.messages.chart")}</Grid>
              </Grid>
            </Hidden>
            {showGraph ? (
              <BarChart
                chartData={{
                  labels: Object.keys(populateProfitPerStore()).map(p => p),
                  datasets: [
                    {
                      label: t("common.tableHeaders.acquisitionCost"),
                      rgbColor: { r: 240, g: 155, b: 13 },
                      data: Object.keys(profitPerStore).map(
                        p => profitPerStore[p].cost
                      ),
                      stack: "stack 0"
                    },
                    {
                      label: "Waste",
                      rgbColor: { r: 195, g: 88, b: 66 },
                      data: Object.keys(profitPerStore).map(
                        p => profitPerStore[p].waste
                      ),
                      stack: "stack 0"
                    },
                    {
                      label: "Campaign",
                      rgbColor: { r: 5, g: 89, b: 141 },
                      data: Object.keys(profitPerStore).map(
                        p => profitPerStore[p].campaign
                      ),
                      stack: "stack 0"
                    },
                    {
                      label: "Coupons",
                      rgbColor: { r: 5, g: 23, b: 141 },
                      data: Object.keys(profitPerStore).map(
                        p => profitPerStore[p].discount
                      ),
                      stack: "stack 0"
                    },
                    {
                      label: "Profit",
                      rgbColor: { r: 31, g: 132, b: 52 },
                      data: Object.keys(profitPerStore).map(
                        p => profitPerStore[p].profit
                      ),
                      stack: "stack 0"
                    },
                    {
                      label: "Sales",
                      rgbColor: { r: 0, g: 148, b: 148 },
                      data: Object.keys(profitPerStore).map(
                        p => profitPerStore[p].sales
                      ),
                      stack: "stack 1"
                    }
                  ]
                }}
                options={{
                  scales: {
                    x: {
                      stacked: true
                    },
                    y: {
                      stacked: true
                    }
                  }
                }}
                showLegend={true}
                isLoading={isLoading}
              />
            ) : (
              <StatTable
                exportName={t("common.statistics.profitStatistics")}
                isLoading={isLoading}
                columns={[
                  {
                    title: "Store",
                    field: "storeName"
                  },
                  {
                    title: "Sales",
                    field: "sales",
                    render: (rowData: any) => <Currency value={rowData.sales} />
                  },
                  {
                    title: t("common.tableHeaders.acquisitionCost"),
                    field: "cost",
                    hidden: mobile,
                    render: (rowData: any) => <Currency value={rowData.cost} />
                  },
                  {
                    title: "Waste",
                    field: "waste",
                    hidden: mobile,
                    render: (rowData: any) => <Currency value={rowData.waste} />
                  },
                  {
                    title: "Coupons",
                    field: "discount",
                    hidden: mobile,
                    render: (rowData: any) => (
                      <Currency value={rowData.discount} />
                    )
                  },
                  {
                    title: "Campaign",
                    field: "campaign",
                    hidden: mobile,
                    render: (rowData: any) => (
                      <Currency value={rowData.campaign} />
                    )
                  },
                  {
                    title: "Profit",
                    field: "profit",
                    cellStyle: {
                      padding: "8px",
                      borderLeft: "1px solid #e9e5e2",
                      backgroundColor: "rgba(5,89,141, 0.1)"
                    },
                    render: (rowData: any) => (
                      <Currency value={rowData.profit} />
                    )
                  }
                ]}
                data={!isLoading ? populateProfitTable() : []}
                hideDetailPanel={mobile}
                detailPanel={[
                  {
                    render: (rowData: any) => (
                      <ProfitDetails
                        profitPerCategory={rowData.categoryProfit}
                        productProfit={{
                          storeId: rowData.storeId,
                          periodFrom: dateRange[0],
                          periodTo: dateRange[1]
                        }}
                      />
                    )
                  }
                ]}
              />
            )}
          </TableWrapper>
        </Grid>
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12} lg={6}>
            <BarChart
              title={"Profit / Store"}
              chartData={{
                labels: Object.keys(populateProfitPerStore()).map(p => p),
                datasets: [
                  {
                    label: "",
                    data: Object.keys(profitPerStore).map(
                      p => profitPerStore[p].profit
                    )
                  }
                ]
              }}
              showLegend={false}
              isLoading={isLoading}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <BarChart
              title={"Profit / Category"}
              chartData={populateChartData()}
              showLegend={false}
              isLoading={isLoading}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
